import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import type { MatChipListboxChange } from '@angular/material/chips';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

type Lang = 'nl' | 'en' | 'de' | 'it' | 'fr';

@Component({
  selector: 'app-localized-list-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './localized-list-editor.component.html',
  imports: [
    MatChipsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslatePipe,
  ],
})
export class LocalizedListEditorComponent {
  readonly nl = input.required<FormControl<string[] | null>>();
  readonly en = input.required<FormControl<string[] | null>>();
  readonly de = input.required<FormControl<string[] | null>>();
  readonly it = input.required<FormControl<string[] | null>>();
  readonly fr = input.required<FormControl<string[] | null>>();

  readonly langs: Lang[] = ['nl', 'en', 'de', 'it', 'fr'];
  readonly activeLang = signal<Lang>('nl');

  readonly activeControl = computed<FormControl<string[] | null>>(() => this.controlFor(this.activeLang()));
  private readonly _refresh = signal(0);
  readonly activeItems = computed<string[]>(() => {
    this._refresh();
    return this.activeControl().value ?? [];
  });

  controlFor(lang: Lang): FormControl<string[] | null> {
    const map: Record<Lang, FormControl<string[] | null>> = {
      nl: this.nl(),
      en: this.en(),
      de: this.de(),
      it: this.it(),
      fr: this.fr(),
    };
    return map[lang];
  }

  onLangChange(event: MatChipListboxChange): void {
    if (event.value != null) {
      this.activeLang.set(event.value as Lang);
    }
  }

  addItem(): void {
    const ctrl = this.activeControl();
    ctrl.setValue([...(ctrl.value ?? []), '']);
    this._refresh.update((n) => n + 1);
  }

  updateItem(index: number, value: string): void {
    const ctrl = this.activeControl();
    const items = [...(ctrl.value ?? [])];
    items[index] = value;
    ctrl.setValue(items);
  }

  removeItem(index: number): void {
    const ctrl = this.activeControl();
    const items = (ctrl.value ?? []).filter((_, i) => i !== index);
    ctrl.setValue(items.length > 0 ? items : null);
    this._refresh.update((n) => n + 1);
  }
}
